import React from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  Stack,
  Paper,
  Avatar,
  Chip,
  Divider,
  IconButton,
  useTheme,
  alpha,
  useMediaQuery,
  Container
} from '@mui/material';
import {
  ErrorOutline as ErrorIcon,
  AccessTime as TimeIcon,
  ContactSupport as SupportIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  CalendarMonth as CalendarIcon,
  Warning as WarningIcon,
  WhatsApp as WhatsAppIcon,
  Support as HelpIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Trans } from '../i18n/Trans';
import { getLocale } from '../utils/locale';

interface SubscriptionExpiredModalProps {
  open: boolean;
  onClose?: () => void;
  endDate?: string;
  daysExpired?: number;
  username?: string;
}

const SubscriptionExpiredModal: React.FC<SubscriptionExpiredModalProps> = ({
  open,
  onClose,
  endDate,
  daysExpired = 0,
  username
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const formatDate = (dateString?: string) => {
    if (!dateString) return t('Belirtilmemiş');
    const date = new Date(dateString);
    return date.toLocaleDateString(getLocale(), {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <Dialog 
      open={open}
      onClose={() => {}} // Prevent closing by clicking outside
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 4,
          overflow: 'hidden',
          background: theme.palette.mode === 'dark' 
            ? `linear-gradient(135deg, ${alpha('#1a1a1a', 0.95)} 0%, ${alpha('#2d2d2d', 0.95)} 100%)`
            : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
          backdropFilter: 'blur(20px)',
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }
      }}
    >
      <DialogContent sx={{ p: 0, position: 'relative' }}>
        {/* Animated Background Pattern */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 200,
            background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.1)} 0%, ${alpha(theme.palette.warning.main, 0.1)} 100%)`,
            zIndex: 0,
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23${theme.palette.error.main.replace('#', '')}' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
              opacity: 0.5,
            }
          }}
        />
        {/* Professional Close Button */}
        {onClose && (
          <IconButton
            onClick={onClose}
            sx={{
              position: 'fixed',
              right: { xs: 12, sm: 16 },
              top: { xs: 12, sm: 16 },
              width: { xs: 36, sm: 40 },
              height: { xs: 36, sm: 40 },
              color: theme.palette.text.secondary,
              bgcolor: alpha(theme.palette.background.paper, 0.8),
              backdropFilter: 'blur(8px)',
              border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
              boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.1)}`,
              zIndex: 1500,
              '&:hover': {
                bgcolor: alpha(theme.palette.background.paper, 0.95),
                borderColor: alpha(theme.palette.primary.main, 0.3),
                color: theme.palette.primary.main,
                transform: 'scale(1.05)',
                boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.15)}`,
              },
              '&:active': {
                transform: 'scale(0.98)',
              },
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            <CloseIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
          </IconButton>
        )}
        
        <Container maxWidth="md" sx={{ py: { xs: 3, sm: 4, md: 5 }, px: { xs: 2, sm: 3 }, width: '100%' }}>
          {/* Header Section */}
          <Box sx={{ textAlign: 'center', mb: 4, position: 'relative', zIndex: 1 }}>
            {/* Animated Icon */}
            <Box
              sx={{
                display: 'inline-flex',
                position: 'relative',
                mb: 3,
              }}
            >
              <Avatar
                sx={{
                  width: { xs: 80, sm: 100 },
                  height: { xs: 80, sm: 100 },
                  bgcolor: alpha(theme.palette.error.main, 0.1),
                  backdropFilter: 'blur(10px)',
                  border: `2px solid ${alpha(theme.palette.error.main, 0.2)}`,
                  animation: 'pulse 2s infinite',
                  '@keyframes pulse': {
                    '0%': {
                      boxShadow: `0 0 0 0 ${alpha(theme.palette.error.main, 0.4)}`,
                    },
                    '70%': {
                      boxShadow: `0 0 0 20px ${alpha(theme.palette.error.main, 0)}`,
                    },
                    '100%': {
                      boxShadow: `0 0 0 0 ${alpha(theme.palette.error.main, 0)}`,
                    },
                  },
                }}
              >
                <ErrorIcon sx={{ fontSize: { xs: 40, sm: 50 }, color: theme.palette.error.main }} />
              </Avatar>
              
              {/* Decorative circles */}
              <Box
                sx={{
                  position: 'absolute',
                  top: -10,
                  right: -10,
                  width: 30,
                  height: 30,
                  borderRadius: '50%',
                  bgcolor: alpha(theme.palette.warning.main, 0.2),
                  animation: 'float 3s ease-in-out infinite',
                  '@keyframes float': {
                    '0%, 100%': { transform: 'translateY(0px)' },
                    '50%': { transform: 'translateY(-10px)' },
                  },
                }}
              />
            </Box>

            <Typography 
              variant={isMobile ? "h5" : "h4"} 
              fontWeight="bold" 
              gutterBottom
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.error.main} 0%, ${theme.palette.error.dark} 100%)`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              {t('Abonelik Süreniz Dolmuş')}
            </Typography>
            
            {username && (
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                <Trans i18nKey="Merhaba, <b>{{username}}</b>" values={{ username }} components={{ b: <strong /> }} />
              </Typography>
            )}
            {/* Status Chips */}
            <Stack 
              direction={{ xs: 'column', sm: 'row' }} 
              spacing={1} 
              justifyContent="center" 
              sx={{ mb: 3 }}
              alignItems="center"
            >
              <Chip
                icon={<CalendarIcon />}
                label={t('Son Tarih: {{date}}', { date: formatDate(endDate) })}
                color="error"
                variant="outlined"
                sx={{ 
                  borderWidth: 2,
                  fontWeight: 500,
                  px: 1
                }}
              />
              {daysExpired > 0 && (
                <Chip
                  icon={<TimeIcon />}
                  label={t('{{daysExpired}} Gün Önce Sona Erdi', { daysExpired, count: daysExpired })}
                  sx={{ 
                    bgcolor: alpha(theme.palette.warning.main, 0.1),
                    color: theme.palette.warning.dark,
                    fontWeight: 500,
                    px: 1
                  }}
                />
              )}
            </Stack>
          </Box>

          {/* Main Content */}
          {/* Warning Card */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.05)} 0%, ${alpha(theme.palette.warning.main, 0.1)} 100%)`,
              borderLeft: `4px solid ${theme.palette.warning.main}`,
              borderRadius: 2,
            }}
          >
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <WarningIcon sx={{ color: theme.palette.warning.main, mt: 0.5 }} />
              <Box>
                <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                  {t('Hizmet Erişiminiz Durduruldu')}
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  {t('Abonelik süreniz sona erdiği için WhatsApp Business API hizmetlerimize erişiminiz geçici olarak durdurulmuştur.')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('Hizmetlerimizden yararlanmaya devam etmek için lütfen aşağıdaki iletişim kanallarından bizimle iletişime geçin.')}
                </Typography>
              </Box>
            </Stack>
          </Paper>

          {/* Contact Options - New Design */}
          <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
              <Paper
                elevation={0}
                sx={{
                  width: '100%',
                  maxWidth: 'none',
                  margin: '0 auto',
                  p: { xs: 3, sm: 4 },
                  background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.03)} 0%, ${alpha(theme.palette.info.main, 0.08)} 100%)`,
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.info.main, 0.1)}`,
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: -2,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '60%',
                    height: 4,
                    background: `linear-gradient(90deg, ${theme.palette.info.main} 0%, ${theme.palette.primary.main} 100%)`,
                    borderRadius: '0 0 8px 8px',
                  }
                }}
              >
                {/* Header with Icon */}
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                  <Avatar
                    sx={{
                      width: 60,
                      height: 60,
                      mx: 'auto',
                      mb: 2,
                      bgcolor: alpha(theme.palette.info.main, 0.1),
                      border: `2px solid ${alpha(theme.palette.info.main, 0.2)}`,
                      animation: 'bounce 2s infinite',
                      '@keyframes bounce': {
                        '0%, 20%, 50%, 80%, 100%': {
                          transform: 'translateY(0)',
                        },
                        '40%': {
                          transform: 'translateY(-8px)',
                        },
                        '60%': {
                          transform: 'translateY(-4px)',
                        },
                      },
                    }}
                  >
                    <SupportIcon sx={{ fontSize: 32, color: theme.palette.info.main }} />
                  </Avatar>
                  <Typography 
                    variant="h5" 
                    fontWeight="700"
                    sx={{ 
                      mb: 1,
                      background: `linear-gradient(135deg, ${theme.palette.info.main} 0%, ${theme.palette.primary.main} 100%)`,
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}
                  >
                    {t('Destek Ekibimizle İletişime Geçin')}
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 400, mx: 'auto' }}>
                    {t('Hızlı çözüm için aşağıdaki kanallardan bizimle iletişime geçebilirsiniz')}
                  </Typography>
                </Box>

                {/* Contact Methods */}
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'column', sm: 'row' }, 
                  gap: 3,
                  justifyContent: 'center'
                }}>
                  {/* Email Contact */}
                  <Box
                      onClick={() => window.location.href = 'mailto:destek@dijitalklinik.com'}
                      sx={{
                        p: 3,
                        background: theme.palette.background.paper,
                        borderRadius: 2,
                        border: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                        cursor: 'pointer',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        position: 'relative',
                        overflow: 'hidden',
                        width: '100%',
                        maxWidth: { xs: 400, sm: 300 },
                        '&:hover': {
                          transform: 'translateY(-4px) scale(1.02)',
                          boxShadow: `0 12px 40px ${alpha(theme.palette.primary.main, 0.2)}`,
                          borderColor: theme.palette.primary.main,
                          '&::before': {
                            opacity: 1,
                          }
                        },
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: 3,
                          background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                          opacity: 0,
                          transition: 'opacity 0.3s ease',
                        }
                      }}
                    >
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Avatar
                          sx={{
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                            width: 50,
                            height: 50,
                          }}
                        >
                          <EmailIcon sx={{ color: theme.palette.primary.main, fontSize: 24 }} />
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h6" fontWeight="600" gutterBottom>
                            {t('E-posta')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            destek@dijitalklinik.com
                          </Typography>
                          <Typography variant="caption" color="primary.main" fontWeight="500">
                            {t('Ortalama yanıt süresi: 2 saat')}
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>

                  {/* Phone Contact */}
                  <Box
                      onClick={() => window.location.href = 'tel:+905431555634'}
                      sx={{
                        p: 3,
                        background: theme.palette.background.paper,
                        borderRadius: 2,
                        border: `2px solid ${alpha(theme.palette.success.main, 0.1)}`,
                        cursor: 'pointer',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        position: 'relative',
                        overflow: 'hidden',
                        width: '100%',
                        maxWidth: { xs: 400, sm: 300 },
                        '&:hover': {
                          transform: 'translateY(-4px) scale(1.02)',
                          boxShadow: `0 12px 40px ${alpha(theme.palette.success.main, 0.2)}`,
                          borderColor: theme.palette.success.main,
                          '&::before': {
                            opacity: 1,
                          }
                        },
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: 3,
                          background: `linear-gradient(90deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
                          opacity: 0,
                          transition: 'opacity 0.3s ease',
                        }
                      }}
                    >
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Avatar
                          sx={{
                            bgcolor: alpha(theme.palette.success.main, 0.1),
                            border: `2px solid ${alpha(theme.palette.success.main, 0.2)}`,
                            width: 50,
                            height: 50,
                          }}
                        >
                          <PhoneIcon sx={{ color: theme.palette.success.main, fontSize: 24 }} />
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h6" fontWeight="600" gutterBottom>
                            {t('Telefon')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            +90 543 155 56 34
                          </Typography>
                          <Typography variant="caption" color="success.main" fontWeight="500">
                            {t('7/24 Anlık Destek')}
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>
                </Box>

                {/* Additional Info */}
                <Divider sx={{ my: 3 }} />
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('Çalışma Saatleri: Pazartesi - Cuma, 09:00 - 18:00')}
                  </Typography>
                </Box>
              </Paper>
          </Box>
        </Container>
      </DialogContent>
    </Dialog>
  );
};

export default SubscriptionExpiredModal;